import React, { Component } from "react";
import { DialogTitle, DialogActions, Button, TextField, Typography } from "@material-ui/core";
import { ColGrid, RowGrid } from "js/react/components/LayoutGrid";
import {
    BeautifulDialog,
    DialogContent,
    ShowSnackBar
} from "js/react/components/Dialogs/BaseDialog";
import { Gap20, Gap10 } from "js/react/components/Gap";
import CheckBox from "js/react/components/CheckBox";
import linkDS from "js/react/views/PresentationSettings/dataservices/LinkDataService";
import { ToggleAllowCommenting } from "../components/ToggleAllowCommenting";
import { ClipboardType, clipboardWrite } from "js/core/utilities/clipboard";
import { PresentationPrivacyType } from "common/constants";
import { domainRegex } from "common/regex";
import isValidDomain from "is-valid-domain";

export default class EditEmbedCodeDialog extends Component {
    constructor(props) {
        super(props);

        const { link } = props;

        const embedOptions = link.get("embedOptions");
        const domainWhitelist = link.get("domainWhitelist");

        this.state = {
            useCustomSize: embedOptions.useCustomSize ?? false,
            width: embedOptions.width ?? 640,
            height: embedOptions.height ?? 360,
            domainWhitelist,
            saveError: null
        };
    }

    get isSecured() {
        return this.props.presentation.getPrivacySetting() === PresentationPrivacyType.SECURED;
    }

    handleAccept = link => {
        const embedOptions = {
            width: this.state.width,
            height: this.state.height,
            useCustomSize: this.state.useCustomSize,
        };
        const domainWhitelist = this.state.domainWhitelist;

        if (this.isSecured && !domainWhitelist) {
            this.setState({ saveError: "Secured presentations require domain whitelist to embed." });
            return;
        }

        linkDS.updateLink(link, { embedOptions, domainWhitelist });
        this.copyToClipboard();
        this.props.closeDialog();
        this.props.callback();
    };

    handleOnEnter = embedOptions => {
        this.setState(embedOptions);
    };

    handleNameChange = name => {
        this.props.link.update({ name });
        this.update();
    }

    toggleAllowCommenting = event => {
        const { link } = this.props;
        if (link) {
            const allowCommenting = !link.get("allowCommenting");
            link.update({ allowCommenting });
            this.update();
        }
    }

    copyToClipboard() {
        const {
            link, presentation
        } = this.props;
        const {
            useCustomSize,
            width,
            height,
        } = this.state;
        const embedCode = presentation.getEmbedCode(
            link,
            {
                useCustomSize,
                width,
                height,
            },
        );
        clipboardWrite(
            {
                [ClipboardType.HTML]: embedCode,
                [ClipboardType.TEXT]: embedCode,
            },
            true,
        );
        ShowSnackBar({
            message: `Link for ${link.get("name")} was copied to the clipboard`
        });
    }

    handleCustomSize(value, type) {
        if (type == "width") {
            this.setState({
                width: value,
                height: value * 0.5625
            });
        } else {
            this.setState({
                height: value,
                width: value * 1.7777777778
            });
        }
    }

    update = () => {
        this.forceUpdate();
        this.props.callback();
    }

    validateDomainWhitelist() {
        const domainWhitelist = this.state.domainWhitelist.trim().replace(/\s/g, "").split(",");

        if (domainWhitelist.some(domain => !domainRegex.test(domain) || !isValidDomain(domain))) {
            return "Invalid domain.";
        }
    }

    render() {
        const { link } = this.props;
        const { saveError, domainWhitelist } = this.state;

        const allowCommenting = link.get("allowCommenting");

        return (
            <BeautifulDialog
                closeDialog={this.props.closeDialog}
                onEnter={() => this.handleOnEnter(link.get("embedOptions"))}
            >
                <DialogTitle>Edit Embed Link</DialogTitle>
                <DialogContent className="dialog-embed">
                    <RowGrid gap={0}>
                        <TextField
                            label="Link Name"
                            value={link.get("name")}
                            onChange={event => this.handleNameChange(event.target.value)}
                        />
                        <Gap20 />
                        <ToggleAllowCommenting
                            allowCommenting={allowCommenting}
                            onToggle={this.toggleAllowCommenting}
                        />
                        {this.isSecured && <>
                            <Gap10 />
                            <TextField
                                id="domainWhitelist"
                                label="Domain Whitelist"
                                placeholder="beautiful.ai"
                                value={domainWhitelist}
                                type="text"
                                className="textField"
                                fullWidth
                                onChange={event =>
                                    this.setState({ domainWhitelist: event.target.value })
                                }
                                onBlur={event => {
                                    const saveError = this.validateDomainWhitelist();
                                    this.setState({ saveError });
                                }}
                            />
                            {saveError &&
                                <Typography variant="caption" color="error">
                                    {saveError}
                                </Typography>}
                        </>}
                        <Gap10 />
                        <ColGrid>
                            <CheckBox
                                checked={this.state.useCustomSize}
                                value="useCustomSize"
                                label="Use Custom Size"
                                onChange={value =>
                                    this.setState({ useCustomSize: value })
                                }
                            />
                            {this.state.useCustomSize &&
                                <>
                                    <TextField
                                        id="width"
                                        label="Width"
                                        placeholder="auto"
                                        value={this.state.width}
                                        type="number"
                                        className="textField"
                                        onChange={event =>
                                            this.handleCustomSize(event.target.value, "width")
                                        }
                                    />
                                    <TextField
                                        id="height"
                                        label="Height"
                                        placeholder="auto"
                                        value={this.state.height}
                                        type="number"
                                        className="textField"
                                        onChange={event =>
                                            this.handleCustomSize(event.target.value, "height")
                                        }
                                    />
                                </>
                            }
                        </ColGrid>
                    </RowGrid>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => this.handleAccept(link)}
                        color="secondary"
                        disabled={(this.isSecured && (!domainWhitelist || saveError))}
                    >
                        Copy Embed Code
                    </Button>
                    <Button
                        onClick={() => this.handleAccept(link)}
                        color="primary"
                        disabled={(this.isSecured && (!domainWhitelist || saveError))}
                    >
                        Done
                    </Button>
                </DialogActions>
            </BeautifulDialog>
        );
    }
}
